import React from 'react';

/**
 * Reusable timeline progress tracker
 * Props:
 *   stages: [{ id, label, icon }]
 *   currentStage: string (id of current stage)
 *   completedStages: string[] (ids of completed stages)
 *   stageData: { [stageId]: { date, note, by_name } }
 *   orientation: 'horizontal' | 'vertical' | 'auto' (default auto)
 *   compact: boolean (smaller version for inline use)
 */
function TimelineProgress({ stages = [], currentStage, completedStages = [], stageData = {}, orientation = 'auto', compact = false }) {
  if (!stages || stages.length === 0) return null;

  const isVertical = orientation === 'vertical' || (orientation === 'auto' && stages.length > 6);
  const completedSet = new Set(completedStages);

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d).toLocaleDateString([], { month: 'short', day: 'numeric' });
    } catch { return ''; }
  };

  if (isVertical) {
    return (
      <div className="tl-vertical">
        {stages.map((stage, i) => {
          const isCompleted = completedSet.has(stage.id);
          const isCurrent = stage.id === currentStage;
          const data = stageData[stage.id] || {};
          const dotClass = isCompleted ? 'tl-dot completed' : isCurrent ? 'tl-dot current' : 'tl-dot future';

          return (
            <div key={stage.id} className="tl-v-step">
              <div className="tl-v-line-wrap">
                <div className={dotClass}>{isCompleted ? '✓' : stage.icon || '○'}</div>
                {i < stages.length - 1 && <div className={`tl-v-line ${isCompleted ? 'completed' : ''}`} />}
              </div>
              <div className="tl-v-content">
                <div className={`tl-v-label ${isCurrent ? 'current' : ''} ${isCompleted ? 'completed' : ''}`}>
                  {stage.label}
                  {data.date && <span className="tl-date">{formatDate(data.date)}</span>}
                </div>
                {data.note && <div className="tl-note">{data.note}</div>}
                {data.by_name && <div className="tl-by">— {data.by_name}</div>}
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  // Horizontal
  return (
    <div className={`tl-horizontal ${compact ? 'tl-compact' : ''}`}>
      {stages.map((stage, i) => {
        const isCompleted = completedSet.has(stage.id);
        const isCurrent = stage.id === currentStage;
        const data = stageData[stage.id] || {};
        const dotClass = isCompleted ? 'tl-dot completed' : isCurrent ? 'tl-dot current' : 'tl-dot future';

        return (
          <React.Fragment key={stage.id}>
            <div className="tl-h-step">
              <div className={dotClass}>
                {compact ? '' : isCompleted ? '✓' : stage.icon || ''}
              </div>
              <div className={`tl-h-label ${isCurrent ? 'current' : ''} ${isCompleted ? 'completed' : ''}`}>
                {stage.label}
              </div>
              {!compact && data.date && <div className="tl-date">{formatDate(data.date)}</div>}
              {!compact && data.note && <div className="tl-note" style={{ maxWidth: 120 }}>{data.note.length > 40 ? data.note.slice(0, 40) + '…' : data.note}</div>}
            </div>
            {i < stages.length - 1 && (
              <div className={`tl-h-line ${isCompleted ? 'completed' : ''}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default TimelineProgress;
